import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import PageShell from '../components/PageShell';
import DentistDashboard from '../components/DentistDashboard';
import {useAuth} from '../hooks/AuthContext';
import * as ReservationApi from '../api/ReservationApi';
import {hubReservationId, hubTargetFor} from '../consultation/deepLinks';
import './VirtualConsultation.css';

/** Space/Enter activate a card that is exposed as a button. */
const activate = (go) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    go();
  }
};

/**
 * Hub. A notification link `?reservation=<id>` is resolved first: a reservation that can be
 * joined now opens its room, any other one opens its detail on the record page. An invalid or
 * unknown id (lookup fails) shows the hub itself.
 */
export default function VirtualConsultation() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const deepLinkId = hubReservationId(location.search);
  const [resolving, setResolving] = useState(Boolean(deepLinkId));

  useEffect(() => {
    if (!deepLinkId) return undefined;
    let cancelled = false;
    ReservationApi.getReservation(deepLinkId)
      .then((reservation) => {
        if (!cancelled) navigate(hubTargetFor(deepLinkId, reservation, Date.now()), {replace: true});
      })
      .catch(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deepLinkId, navigate]);

  return (
    <PageShell title="Virtual Consultation" showBackButton={true}>
      {auth?.user?.doctor && <DentistDashboard />}
      <div className="vc-container">
        {resolving ? (
          <p role="status" className="vc-section-title">
            Opening your consultation…
          </p>
        ) : (
          <div className="vc-section">
            <h2 className="vc-section-title">Talk to a dentist without the trip</h2>

            <div className="vc-cards">
              {/* Book a consultation */}
              <div
                className="vc-card"
                role="button"
                tabIndex={0}
                onClick={() => {
                  navigate('/virtual-consultation/book');
                }}
                onKeyDown={activate(() => navigate('/virtual-consultation/book'))}
              >
                <div>
                  <h3>Book a consultation</h3>
                  <p>Schedule a virtual visit with a dentist</p>
                </div>
                <p className="vc-card-action">Pick a time, describe the problem, pay</p>
              </div>

              {/* My consultations */}
              <div
                className="vc-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/virtual-consultation/reservation-record')}
                onKeyDown={activate(() => navigate('/virtual-consultation/reservation-record'))}
              >
                <div>
                  <h3>My consultations</h3>
                  <p>View current and past consultation records</p>
                </div>
                <p className="vc-card-muted">Always available for your reference</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
